import React, { useMemo, useState } from 'react';
import Badge from '@mui/material/Badge';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import MoreHorizOutlined from '@mui/icons-material/MoreHorizOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import {
  AccountCircleOutlined, SettingsOutlined, HelpOutlineOutlined,
  Login, Logout,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import FileFolderEditIcon from '../../../common/Icons/FileFolderEditIcon';
import { HomeIcon, TimeExpenseIcon } from '../../../common/Icons';
import { handleLogin, handleLogout } from '../../../../utils/loginFunctions';
import LanguageSwitcher from '../../../languageSwitcher/LanguageSwitcher';
import configData from '../../../../config.json';

const NAV_BG = '#022D41';
const NAV_INACTIVE = '#7C93A3';
const NAV_ACTIVE = '#FFFFFF';
const NAV_ACCENT = '#0094B3';

function isRouteActive(pathname, value) {
  if (pathname === value) return true;
  if (pathname.startsWith(`${value}/`)) return true;
  const section = value.split('/').pop()?.replace(/s$/, '');
  if (section && section.length > 3 && pathname.includes(`/${section}`)) return true;
  return false;
}

function NavButton({ icon, label, active, badge, onClick }) {
  return (
    <button
      onClick={onClick}
      className="relative flex flex-col items-center justify-center gap-1 flex-1 h-full bg-transparent border-none cursor-pointer active:scale-[0.94] transition-transform duration-150"
    >
      {active && (
        <span className="absolute top-[6px] w-9 h-9 rounded-[12px]" style={{ backgroundColor: 'rgba(255,255,255,0.10)' }} />
      )}
      <span className="relative flex items-center justify-center transition-colors duration-150" style={{ color: active ? NAV_ACTIVE : NAV_INACTIVE }}>
        {badge != null && badge > 0 ? (
          <Badge badgeContent={badge} color="error" max={9} sx={{ '& .MuiBadge-badge': { fontSize: '9px', minWidth: '15px', height: '15px' } }}>
            {icon}
          </Badge>
        ) : icon}
      </span>
      <span className="relative text-[10px] leading-none transition-colors duration-150" style={{ color: active ? NAV_ACTIVE : NAV_INACTIVE, fontWeight: active ? 700 : 500 }}>
        {label}
      </span>
      {active && <span className="absolute -top-[1px] w-5 h-[3px] rounded-full" style={{ backgroundColor: NAV_ACCENT }} />}
    </button>
  );
}

function SheetRow({ icon, label, onClick, href, loading }) {
  const content = (
    <>
      <span className="flex items-center justify-center w-9 h-9 rounded-[10px] shrink-0" style={{ backgroundColor: '#F0F4F7', color: NAV_BG }}>
        {loading ? <CircularProgress size="1rem" sx={{ color: '#0094B3' }} /> : icon}
      </span>
      <span className="text-[15px] font-medium text-[#022D41]">{label}</span>
    </>
  );
  const cls = "flex items-center gap-3 px-5 py-3.5 bg-transparent border-none cursor-pointer text-left hover:bg-[#F8FAFB] active:bg-[#F0F4F7] transition-colors w-full no-underline";

  if (href) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>{content}</a>;
  }
  return <button onClick={onClick} className={cls}>{content}</button>;
}

export default function FooterPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [isBeingSignedIn, setIsBeingSignedIn] = useState(false);

  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const accessTo = useSelector((state) => state.accessToParts);
  const pendingTandECounts = useSelector((state) => state.timesheet?.tandecounts);

  const allItems = useMemo(() => {
    const items = [
      { key: 'home', label: t('common.header.menu.home'), icon: <HomeIcon />, value: '/dashboard' },
    ];
    if (accessTo.tAndEContractor || accessTo.tAndEApprover) {
      items.push({
        key: 'timesheets', label: t('dashboard.timesheets.sidetabTimeShort'), icon: <TimeExpenseIcon />,
        value: '/dashboard/timesheets', badge: pendingTandECounts?.PendingTimesheetCount,
      });
      items.push({
        key: 'expenses', label: t('dashboard.expenses.sidetabExpense'), icon: <PaidOutlinedIcon />,
        value: '/dashboard/expenses', badge: pendingTandECounts?.PendingClaimsCount,
      });
    }
    if (!accessTo.tAndEApprover) {
      items.push({ key: 'jobs', label: t('dashboard.jobs.job'), icon: <WorkOutlineIcon />, value: '/dashboard/jobs' });
    }
    if (accessTo.onboarding) {
      items.push({ key: 'onboarding', label: t('common.header.menu.onboarding'), icon: <FileFolderEditIcon />, value: configData.REACT_APP_TIFO_URL });
    }
    items.push({ key: 'profile', label: t('common.header.menu.profile'), icon: <AccountCircleOutlined />, value: '/my-profile' });
    return items;
  }, [accessTo, pendingTandECounts, t]);

  const primary = allItems.length > 4 ? allItems.slice(0, 4) : allItems;
  const overflowNav = allItems.length > 4 ? allItems.slice(4) : [];
  const activeOverflow = overflowNav.some((item) => isRouteActive(location.pathname, item.value));

  const showSettings = !accessTo.tAndEApprover && isAuthenticated;

  const goTo = (value) => {
    setMoreOpen(false);
    navigate(value);
  };

  const handleAuth = () => {
    setIsBeingSignedIn(true);
    isAuthenticated ? handleLogout(instance) : handleLogin(instance);
  };

  return (
    <>
      <style>{`
        @keyframes sheet-backdrop-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes sheet-slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .sheet-backdrop { animation: sheet-backdrop-in 0.18s ease both; }
        .sheet-panel { animation: sheet-slide-up 0.25s cubic-bezier(0.32,0.72,0,1) both; }
      `}</style>

      <nav
        className="fixed bottom-0 left-0 right-0 z-[100] flex items-stretch"
        style={{ height: '60px', paddingBottom: 'env(safe-area-inset-bottom)', backgroundColor: NAV_BG, boxShadow: '0 -2px 16px rgba(2,45,65,0.25)' }}
      >
        {primary.map((item) => (
          <NavButton
            key={item.key}
            icon={item.icon}
            label={item.label}
            badge={item.badge}
            active={isRouteActive(location.pathname, item.value)}
            onClick={() => goTo(item.value)}
          />
        ))}

        <NavButton
          icon={<MoreHorizOutlined sx={{ fontSize: 22 }} />}
          label={t('common.general.more', 'More')}
          active={activeOverflow}
          onClick={() => setMoreOpen(true)}
        />
      </nav>

      {moreOpen && (
        <>
          <div className="sheet-backdrop fixed inset-0 z-[110] bg-black/40" onClick={() => setMoreOpen(false)} />
          <div
            className="sheet-panel fixed left-0 right-0 bottom-0 z-[120] bg-white rounded-t-[16px] overflow-hidden max-h-[80vh] overflow-y-auto"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 8px)' }}
          >
            <span className="w-10 h-1 rounded-full bg-[#E2E8ED] mx-auto block mt-2.5" />
            <div className="flex items-center justify-between px-5 pt-2 pb-2">
              <h3 className="text-[16px] font-bold text-[#022D41] m-0">{t('common.general.more', 'More')}</h3>
              <button onClick={() => setMoreOpen(false)} className="bg-transparent border-none cursor-pointer text-[#6B7A85] p-1">
                <CloseOutlined sx={{ fontSize: 20 }} />
              </button>
            </div>

            {/* Overflow nav items (e.g. Onboarding/Profile if the tab bar is full) */}
            {overflowNav.length > 0 && (
              <div className="flex flex-col pb-1 border-b border-[#E2E8ED]">
                {overflowNav.map((item) => (
                  <SheetRow key={item.key} icon={item.icon} label={item.label} onClick={() => goTo(item.value)} />
                ))}
              </div>
            )}

            <div className="flex flex-col py-1 border-b border-[#E2E8ED]">
              {showSettings && (
                <SheetRow icon={<SettingsOutlined sx={{ fontSize: 18 }} />} label="Settings" onClick={() => goTo('/dashboard/settings')} />
              )}
              <SheetRow
                icon={<HelpOutlineOutlined sx={{ fontSize: 18 }} />}
                label="Support"
                href="https://www.airswift.com/en/knowledge"
              />
            </div>

            <div className="flex items-center gap-3 px-5 py-3 border-b border-[#E2E8ED]">
              <LanguageSwitcher collapsed={false} />
            </div>

            <div className="flex flex-col py-1">
              <SheetRow
                icon={isBeingSignedIn ? null : (isAuthenticated ? <Logout sx={{ fontSize: 18 }} /> : <Login sx={{ fontSize: 18 }} />)}
                label={isAuthenticated ? 'Sign Out' : 'Sign In'}
                onClick={handleAuth}
                loading={isBeingSignedIn}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
}